/**
 * PrinterManagementPage — `/printing/printers`.
 *
 * Operator manages connected printers:
 *   - List printers (GET /v1/printing/printers)
 *   - Register a new printer (POST /v1/printing/printers)
 *   - Override status / disable (POST /v1/printing/printers/:id/status)
 */
import { type JSX, useState } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Printer, WifiOff, XCircle } from 'lucide-react';

import { listPrinters, registerPrinter, setPrinterStatus } from '../../lib/api/printing';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

export function PrinterManagementPage(): JSX.Element {
  const qc = useQueryClient();

  const printers = useQuery({
    queryKey: ['printing', 'printers'],
    queryFn: () => listPrinters({ pageSize: 200 }),
    retry: false,
  });

  // ── Register form ─────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formKind, setFormKind] = useState<'THERMAL_4x6' | 'THERMAL_88MM' | 'LASER_A4' | 'INKJET_A4'>('THERMAL_4x6');
  const [formEndpoint, setFormEndpoint] = useState('');

  const registerMut = useMutation({
    mutationFn: () =>
      registerPrinter({ printerName: formName, kind: formKind, endpoint: formEndpoint }),
    onSuccess: () => {
      setShowForm(false);
      setFormName('');
      setFormEndpoint('');
      void qc.invalidateQueries({ queryKey: ['printing', 'printers'] });
    },
  });

  // ── Status override ───────────────────────────────────────────────────────
  const statusMut = useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: 'ONLINE' | 'OFFLINE' | 'ERROR' | 'MAINTENANCE';
      reason: string;
    }) => setPrinterStatus(id, status, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['printing', 'printers'] });
    },
  });

  function handleDisable(id: string): void {
    statusMut.mutate({ id, status: 'MAINTENANCE', reason: 'Manually disabled by operator' });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Printer management"
        description="Register, monitor, and manage physical printers."
        actions={
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {showForm ? 'Cancel' : '+ Register printer'}
          </button>
        }
      />

      {/* ── Register form ─────────────────────────────────────── */}
      {showForm && (
        <div className="bg-white border border-border rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Register new printer</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Printer name</label>
              <input
                data-testid="printer-name"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                placeholder="Counter-1-Thermal"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Kind</label>
              <select
                data-testid="printer-kind"
                value={formKind}
                onChange={(e) =>
                  setFormKind(e.target.value as typeof formKind)
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="THERMAL_4x6">THERMAL_4x6</option>
                <option value="THERMAL_88MM">THERMAL_88MM</option>
                <option value="LASER_A4">LASER_A4</option>
                <option value="INKJET_A4">INKJET_A4</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Endpoint (IP / USB)</label>
              <input
                data-testid="printer-endpoint"
                type="text"
                value={formEndpoint}
                onChange={(e) => setFormEndpoint(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                placeholder="192.168.1.50"
              />
            </div>
          </div>
          {registerMut.isError ? (
            <p className="text-xs text-red-600">{(registerMut.error as Error).message}</p>
          ) : null}
          <button
            type="button"
            data-testid="submit-register-printer"
            disabled={registerMut.isPending || !formName || !formEndpoint}
            onClick={() => registerMut.mutate()}
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {registerMut.isPending ? 'Registering…' : 'Register'}
          </button>
        </div>
      )}

      {/* ── Printer list ──────────────────────────────────────── */}
      {printers.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : printers.isError ? (
        <p className="text-sm text-red-600">{(printers.error as Error).message}</p>
      ) : (printers.data?.items.length ?? 0) === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center">
          <Printer className="w-8 h-8 text-muted mx-auto mb-2" />
          <p className="text-sm text-muted">No printers registered yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Endpoint</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Last seen</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {printers.data?.items.map((p) => (
                <tr key={p.id} data-testid={`printer-row-${p.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{p.printerName}</td>
                  <td className="px-4 py-2 text-xs text-muted">{p.kind}</td>
                  <td className="px-4 py-2 text-xs font-mono">{p.endpoint}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1">
                      {p.status === 'ONLINE' ? (
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      ) : p.status === 'OFFLINE' ? (
                        <WifiOff className="w-3 h-3 text-yellow-600" />
                      ) : (
                        <XCircle className="w-3 h-3 text-red-600" />
                      )}
                      <StatusBadge status={p.status} />
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {p.lastHeartbeatAt ? new Date(p.lastHeartbeatAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {p.active && p.status !== 'MAINTENANCE' ? (
                      <button
                        type="button"
                        data-testid={`disable-printer-${p.id}`}
                        onClick={() => handleDisable(p.id)}
                        disabled={statusMut.isPending}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Disable
                      </button>
                    ) : (
                      <span className="text-xs text-muted">Disabled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
